import { motion } from 'framer-motion';
import CatalogueProductCard from '../components/CatalogueProductCard';
import ClientsVendorsSection from '../components/ClientsVendorsSection';

const WorkPage = () => {
  const workCategories = [
    {
      title: 'Corporate Clients',
      desc: 'Trusted by leading companies across India',
      projects: [
        {
          company: 'Tech Solutions Ltd.',
          image: 'https://images.unsplash.com/photo-1758520144424-2e04f4ad7f60?crop=entropy&cs=srgb&fm=jpg&q=85',
          detail: '500+ uniforms for office staff',
        },
        {
          company: 'Hospitality Group',
          image: 'https://images.unsplash.com/photo-1758518730523-c9f6336ebdae?crop=entropy&cs=srgb&fm=jpg&q=85',
          detail: '1000+ hotel staff uniforms',
        },
      ],
    },
    {
      title: 'Schools & Institutions',
      desc: 'Providing quality uniforms for educational institutions',
      projects: [
        {
          company: 'Delhi Public School',
          image: 'https://images.unsplash.com/photo-1758518731468-98e90ffd7430?crop=entropy&cs=srgb&fm=jpg&q=85',
          detail: '2000+ student uniforms annually',
        },
        {
          company: 'St. Xavier\'s College',
          image: 'https://images.unsplash.com/photo-1451930264513-2e93da3ba755?crop=entropy&cs=srgb&fm=jpg&q=85',
          detail: '800+ college blazers and uniforms',
        },
      ],
    },
    {
      title: 'Sports Teams',
      desc: 'Custom team apparel for competitive sports',
      projects: [
        {
          company: 'Mumbai Cricket League',
          image: 'https://images.unsplash.com/photo-1752778598489-ae34ac74ab34?crop=entropy&cs=srgb&fm=jpg&q=85',
          detail: 'Custom jerseys for 12 teams',
        },
        {
          company: 'Corporate Marathon Series',
          image: 'https://images.unsplash.com/photo-1689395218980-2b3ef140d77e?crop=entropy&cs=srgb&fm=jpg&q=85',
          detail: '5000+ running kits',
        },
      ],
    },
    {
      title: 'Bulk Manufacturing',
      desc: 'Large-scale production for distributors and retailers',
      projects: [
        {
          company: 'Retail Chain Partner',
          image: 'https://images.unsplash.com/photo-1684259499086-93cb3e555803?crop=entropy&cs=srgb&fm=jpg&q=85',
          detail: 'Private label sportswear collection',
        },
        {
          company: 'E-commerce Platform',
          image: 'https://images.unsplash.com/photo-1636986056375-184676d8ca14?crop=entropy&cs=srgb&fm=jpg&q=85',
          detail: 'White-label apparel manufacturing',
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="work-page">
      {/* Hero */}
      <section className="section-spacing bg-brand-timberwolf" data-testid="work-hero">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-brand-pearl leading-none mb-6" data-testid="work-hero-heading">
              Our Work & Clients
            </h1>
            <p className="text-base sm:text-lg text-brand-walnut max-w-3xl mx-auto leading-relaxed">
              Trusted by 500+ organizations across India. Here's a showcase of our manufacturing excellence.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Work Categories */}
      {workCategories.map((category, catIdx) => (
        <section
          key={catIdx}
          className={`section-spacing ${catIdx % 2 === 0 ? 'bg-brand-ivory' : 'bg-brand-timberwolf'}`}
          data-testid={`work-category-${catIdx}`}
        >
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-heading font-bold text-brand-pearl mb-4">
                {category.title}
              </h2>
              <p className="text-base sm:text-lg text-brand-walnut">{category.desc}</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {category.projects.map((project, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className="card-hover"
                  data-testid={`project-${catIdx}-${idx}`}
                >
                  <div className="bg-brand-ivory rounded-sm border border-brand-pearl/10 overflow-hidden">
                    <div className="parallax-img">
                      <img src={project.image} alt={project.company} className="w-full h-64 object-cover" />
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-heading font-semibold text-brand-pearl mb-2">
                        {project.company}
                      </h3>
                      <p className="text-sm text-brand-walnut">{project.detail}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* Shop / Catalogue Section */}
      <section className="section-spacing bg-brand-ivory" data-testid="catalogue-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-brand-pearl mb-4" data-testid="catalogue-heading">
              Shop / Catalogue
            </h2>
            <p className="text-base sm:text-lg text-brand-walnut max-w-2xl mx-auto">
              Explore our casual and lifestyle wear collection. All enquiries are handled via WhatsApp for bulk orders.
            </p>
          </motion.div>

          {/* Casual / Lifestyle Wear */}
          <div className="mb-12">
            <h3 className="text-2xl font-heading font-semibold text-brand-pearl mb-8 text-center">
              Casual / Lifestyle Wear
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Initial Product - White Round-Neck T-Shirt */}
              <CatalogueProductCard
                product={{
                  name: 'White Round-Neck Cotton T-Shirt',
                  price: '₹99 per piece',
                  fabric: '100% Cotton',
                  fit: 'Regular Fit',
                  color: 'White',
                  moq: '50 pieces',
                  deliveryTime: 'Within 7 working days',
                  description: 'Premium-quality white round-neck t-shirt manufactured using 100% cotton fabric, designed for bulk orders, promotions, uniforms, and private labeling. Consistent fabric quality, reliable stitching, and fast turnaround time make it suitable for institutional and resale requirements.',
                  image: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?crop=entropy&cs=srgb&fm=jpg&q=85',
                  isPlaceholder: false,
                }}
                index={0}
              />

              {/* Placeholder Products */}
              <CatalogueProductCard
                product={{
                  placeholder: 'Product Coming Soon',
                  isPlaceholder: true,
                }}
                index={1}
              />
              <CatalogueProductCard
                product={{
                  placeholder: 'Add New Product',
                  isPlaceholder: true,
                }}
                index={2}
              />
              <CatalogueProductCard
                product={{
                  placeholder: 'Product Coming Soon',
                  isPlaceholder: true,
                }}
                index={3}
              />
              <CatalogueProductCard
                product={{
                  placeholder: 'Product Coming Soon',
                  isPlaceholder: true,
                }}
                index={4}
              />
              <CatalogueProductCard
                product={{
                  placeholder: 'Add New Product',
                  isPlaceholder: true,
                }}
                index={5}
              />
            </div>
          </div>

          {/* Note about enquiry-based process */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mt-12 p-6 bg-brand-timberwolf rounded-sm border border-brand-pearl/10"
            data-testid="catalogue-note"
          >
            <p className="text-brand-walnut">
              <strong className="text-brand-pearl">Note:</strong> All products are available for bulk orders only. 
              Click "Send Enquiry on WhatsApp" to discuss quantities, customization, and delivery timelines.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Clients & Vendors */}
      <ClientsVendorsSection />

      {/* CTA */}
      <section className="section-spacing bg-brand-pearl text-brand-ivory" data-testid="work-cta">
        <div className="container-custom text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-6">
            Want to Work with Us?
          </h2>
          <p className="text-base sm:text-lg max-w-2xl mx-auto mb-8 opacity-90">
            Join hundreds of satisfied clients who trust Rudra Apparel for their uniform needs.
          </p>
          <a
            href="/contact"
            data-testid="work-cta-btn"
            className="inline-flex items-center justify-center px-8 py-3 bg-brand-ivory text-brand-pearl font-medium rounded-sm hover:bg-brand-timberwolf transition-colors"
          >
            Get in Touch
          </a>
        </div>
      </section>
    </div>
  );
};

export default WorkPage;